import { seg, tween } from '../../../core/effect/motion';
import {
  CARD_ASPECT, add, canvasOf, cardName, faceArt, fade, fill, place, playerColor, sprite,
} from './common';
import {
  FRAME_URL, PANEL, paintPanel, panelReady, panelSpec,
} from './battlePanel';
import { OBJECT_STYLE } from '../drawObjects';
import {
  FLIP_D, FLIP_LAND as LAND, flipFaceScript, flipTweak, liftOf, turnOf,
} from './flipOpen';

/**
 * **表示形式の変更**（Issue #555。指示書 `../direction/pos_change.md`）。モンスターゾーンと**魔法・罠ゾーン**
 * （手札・墓地などゾーン以外は開かない）。魔法・罠の発動は `pos_change`（伏せ → 表）→ `chaining` と続くので、
 * **カードの裏表が変わる動きはここに集約**し、`chaining` は発動の感じだけを持つ。魔法・罠は浮いて開く動きだけで
 * **パネルは出さない**（尺は開く動きの分だけ＝開き終わってから発動が来る）。
 *
 * 盤のカードそのものを動かす（`tweaks` で借りる。R13）:
 *
 * - **裏表が変わる**（裏守 ↔ 表守・裏守 → 表攻・表攻 → 裏守）: 少し浮いて、長い辺を軸に開くように裏返る（440ms。
 *   幅を縮めて開く形。影も一緒に。**反転召喚と共有**＝`../fx/flipOpen.js`）。
 *   前半（まだ前の面が見えている間）は演出の板が**前の面**を描き、盤のカードは隠す。真横を過ぎたら盤のカード
 *   （＝後の面。盤面はイベントの後の状態）を残りの半回転ぶん回して下ろし、そのまま盤に返す
 * - **表のまま向きが変わる**（表守 ↔ 表攻）: 止め → くいっと 90° 回って少し行き過ぎて戻る（280ms）。
 *   カードの短い辺の両端に、回った跡の硬い線
 *
 * ```
 * 0     盤のカードが浮く（裏返る）／ 止め（回る）
 * 60    裏返り始める ／ 40 くいっと回る
 * 320   裏返り終わる → 400 着地（set_card）／ 230 回り終わる
 * --- パネルあり（1300ms）
 * 280   暗転、変わったカードのパネル（攻撃宣言と同じ `../fx/battlePanel.js`）がそのプレイヤーの側から滑り込む
 * 480   叩きつけ → ヒットストップ 80ms（色収差）→ 揺れ
 * 1080  縦に潰れて消える（暗転も明ける）
 * ```
 *
 * **パネルを出さない**: ダメージステップの間（`damage_step_start` 〜 `damage_step_end`）に、効果の処理
 * （`chain_solving` 〜 `chain_solved`）ではなく変わったとき（裏守備を攻撃されて表に返る など）。動きだけ。
 * 状況（`flip` / `turn` / `spell`）とパネルを出すか（`panel`）・パネルの中身（`who`）は分岐レイヤー
 * （`editorPorts/duelSituations/pos_change.js`）が payload に付ける（Issue #589）。
 * 盤に残る対戦の小さいパネル（`battlePanel` の印）は今の盤面の値で描くので、ここでは何もしない。
 */
const TURN_D = 280;
const TURN_IN = 40;
const TURN_OUT = 230;

const PANEL_D = 1300;
const DIM_IN = 280;
const SLIDE = 300;
const HIT = 480;
const STOP = 80;
const EXIT = 1080;
const GONE = 1240;

/** 回った跡の線の数（短い辺の両端に 1 本ずつ）と、弧の分割 */
const ARC_SEGS = 7;

/** 動きの尺 */
const motionMs = (motion) => (motion === 'flip' ? FLIP_D : TURN_D);

/** くいっと回る（止め → 行き過ぎて戻る）。0 → 1（途中で 1 を少し超える） */
const turnAt = (t) => tween(t, TURN_IN, TURN_OUT, 'back_out');

/**
 * 表示形式の変更の組み立て。`variant` = `'flip'`（裏 ↔ 表）/ `'turn'`（攻 ↔ 守）/ `'spell'`（魔法・罠の表裏。パネルは出さない）。
 * **motion と effect の 2 つの帯に分かれる**: `posChangeMotionSpec`（カードが開く・回る。`<situation>_motion.js`）と
 * `posChangeEffectSpec`（変わったあとのパネル。`<situation>_effect.js`。`panel` のときだけ帯を作る）。
 * 同じイベントでは motion → effect の順に並ぶ
 */
const specOf = (variant) => {
  const spell = variant === 'spell';
  const motion = spell ? 'flip' : variant;
  return ({
  label: '表示形式の変更',
  color: '#5aa8ff',
  motionDuration: () => motionMs(motion),
  motionSounds: () => [
    motion === 'flip' ? { at: LAND - 10, name: 'set_card', gain: 0.8 } : { at: TURN_IN, name: 'set_card', gain: 0.7 },
  ],
  effectSounds: () => [{ at: HIT - 10, name: 'set_card', gain: 0.9 }],
  scene: ({ payload }) => (payload?.panel ? ['reset',
    {
      ms: HIT,
      grade: {
        contrast: { keys: [{ ms: 0, value: 1.3 }, { ms: STOP, value: 1.3, ease: 'hold' }, { ms: STOP + 1, value: 1 }] },
        chroma: { keys: [{ ms: 0, value: 0.011 }, { ms: STOP + 100, value: 0, ease: 'ease_out' }] },
      },
    },
    { ms: HIT + STOP, shake: { amp: 18, freq: 26, decayMs: 240 } },
  ] : []),

  /*
   * **盤のカードを借りて動かす**（handle は `stage/three/runtime.js::handleFor`）。
   * 盤のカードはイベントの後の状態（後の面・後の向き・後の浮き）で置かれているので、そこからの差で動かす。
   * 動きが終わったら何も返さない → runtime が素の置き方に戻す（盤に返す）。
   */
  tweaks: ({ payload, localMs: t, style }) => {
    if (!payload?.uid || t >= motionMs(motion)) return {};
    const prev = payload.prevPosition;
    const next = payload.position;
    const dYaw = turnOf(prev, spell) - turnOf(next, spell);
    const dLift = liftOf(prev, style, spell) - liftOf(next, style, spell);
    if (motion === 'flip') {
      return { [payload.uid]: (h) => flipTweak(h, { t, prev, next, spell, style }) };
    }
    return {
      [payload.uid]: (h) => {
        /*
         * **毎フレーム素の置き方から組む**（`h.reset()`）。触るのは runtime が素に戻すものだけ
         * （root の位置・向き・倍率、stack の位置、素材の濃さ）—— 途中から帯の外へシークしても盤のカードが崩れたまま残らない
         */
        h.reset?.();
        const stack = h.stack ?? h.root;
        const k = turnAt(t);
        h.root.rotation.y += dYaw * (1 - k);
        stack.position.y += dLift * (1 - Math.min(1, k));
      },
    };
  },

  layers: [
    {
      id: 'card',
      label: '裏返る前の面・回った跡の線',
      space: 'board',
      script: (ctx) => {
        const { payload } = ctx;
        if (motion === 'flip') {
          // 前の面（裏ならスリーブ）。前半だけ見せる
          return flipFaceScript(ctx, {
            prev: payload.prevPosition, next: payload.position, spell: spell, code: payload.who?.code ?? payload.code ?? null,
          });
        }
        const { THREE, kit } = ctx;
        const root = new THREE.Group();
        const f = ctx.stage?.field;
        const unit = f?.unit > 0 ? f.unit : 0;
        const plane = f?.plane ?? null;
        const at = payload?.uid ? ctx.anchor?.({ card: payload.uid, else: { zone: payload.zone ?? '' } }) : null;
        if (!at?.plane || !(unit > 0) || !plane) return { root };
        const prev = payload.prevPosition;
        const next = payload.position;
        const base = Number(f.cardAngle?.(at.zone ?? payload.zone)) || 0;
            const yaw0 = base + turnOf(prev, spell);
        const yaw1 = base + turnOf(next, spell);
        const thick = { ...OBJECT_STYLE, ...(ctx.style ?? {}) }.cardThickness;
        const [wx, wz] = [at.plane[0] - plane.w / 2, at.plane[1] - plane.h / 2];
        const cw = unit;
        const ch = unit / CARD_ASPECT;

        // 回った跡: 短い辺の両端が描く弧（回った後ろに硬い線を引き、すぐ細くなって消える）
        const r = ch / 2;
        const arcs = [0, 1].map((end) => Array.from({ length: ARC_SEGS }, (_, i) => {
          const m = kit.onBoard(fill(ctx, i === ARC_SEGS - 1 ? '#ffd21a' : '#ffffff', 1, 1));
          return { m: add(root, m, 30 + end * 0.01 + i * 0.001), i, end };
        })).flat();
        const y = liftOf(next, ctx.style, spell) + thick + 2;
        return {
          root,
          update: ({ localMs: t }) => {
            const k = turnAt(t);
            const yaw = yaw1 + (yaw0 - yaw1) * (1 - k);
            // 跡の長さ（回る間に伸び、回り終わると縮む）
            const trail = (yaw1 - yaw0) * Math.min(0.55, seg(t, TURN_IN, TURN_IN + 60) * 0.55) * (1 - seg(t, TURN_IN + 110, TURN_OUT + 30));
            const thin = 1 - seg(t, TURN_OUT - 40, TURN_D - 10);
            arcs.forEach(({ m, i, end }) => {
              const a0 = yaw - trail * (i / ARC_SEGS);
              const a1 = yaw - trail * ((i + 1) / ARC_SEGS);
              // カードの長い軸（絵の上）が向く方向 = (-sin, -cos)。反対の端はその逆
              const s = end ? -1 : 1;
              const p0 = [wx - s * r * Math.sin(a0), wz - s * r * Math.cos(a0)];
              const p1 = [wx - s * r * Math.sin(a1), wz - s * r * Math.cos(a1)];
              const len = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]);
              m.position.set((p0[0] + p1[0]) / 2, y, (p0[1] + p1[1]) / 2);
              m.rotation.y = -Math.atan2(p1[1] - p0[1], p1[0] - p0[0]);
              m.scale.set(Math.max(0.001, len * 1.08), 1, Math.max(0.001, cw * 0.05 * (1 - i / (ARC_SEGS + 1)) * thin));
              fade(m, t >= TURN_IN && len > 0.5 && thin > 0.01 ? 1 : 0);
            });
          },
        };
      },
    },
    {
      id: 'panel',
      label: '変わったカードのパネル（攻撃宣言と同じ作り）',
      space: 'screen',
      script: (ctx) => {
        const { THREE, kit } = ctx;
        const root = new THREE.Group();
        const { payload } = ctx;
        if (!payload?.panel || !payload.who) return { root };
        const { w: W, h: H } = canvasOf(ctx);
        const player = payload.player === 1 ? 1 : 0;
        const side = player === 1 ? 1 : -1;
        const s0 = panelSpec(payload.who, { attacker: false, player, side });
        const spec = { ...s0, color: playerColor(ctx, player), name: s0.faceDown ? s0.name : cardName(ctx, s0.code) };
        const art = faceArt(ctx, { code: spec.code, player, faceDown: spec.faceDown });
        const imgs = { art };

        const dim = add(root, place(fill(ctx, '#040406', W, H), W / 2, H / 2), 0);
        // 攻撃宣言の直接攻撃の攻撃側パネルと同じ位置（そのプレイヤーの側）
        const GAP = 140;
        const CY = H * 0.56 - PANEL.nameH / 2;
        const frameCY = CY + PANEL.nameH / 2;
        const hx = W / 2 + side * (PANEL.w / 2 + GAP / 2);

        const g = new THREE.Group();
        add(root, g, 10);
        const layer = (name, o) => {
          const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
          if (c) { c.width = PANEL.w; c.height = Math.ceil(PANEL.h); }
          const tex = c ? kit.textureFromCanvas(c) : null;
          const paint = () => {
            const g2 = c?.getContext?.('2d');
            if (!g2) return;
            g2.setTransform(1, 0, 0, 1, 0, 0);
            g2.clearRect(0, 0, c.width, c.height);
            paintPanel(g2, spec, name, imgs);
            if (tex) tex.needsUpdate = true;
          };
          paint();
          const mesh = kit.quad(kit.plain(tex, 1), PANEL.w, Math.ceil(PANEL.h));
          mesh.renderOrder = 10 + o;
          g.add(mesh);
          return { mesh, paint };
        };
        const base = layer('base', 0.01);
        const small = layer('small', 0.02);
        const flash = sprite(ctx, kit.urlTexture(FRAME_URL), PANEL.w, PANEL.frameH, { blend: 'add' });
        flash.position.set(0, PANEL.nameH / 2, 0);
        flash.scale.set(side === 1 ? -1 : 1, 1, 1);
        flash.renderOrder = 10.03;
        g.add(flash);
        let ready = JSON.stringify(panelReady(imgs));

        // 滑り込むときの硬い線（3 本）
        const trails = [-0.3, 0.05, 0.34].map((k, i) => ({ m: add(root, fill(ctx, i === 1 ? '#ffd21a' : '#ffffff', 1, 1), 9), k }));

        return {
          root,
          update: ({ localMs: t }) => {
            const r = JSON.stringify(panelReady(imgs));
            if (r !== ready) { ready = r; base.paint(); small.paint(); }
            const u = t < HIT ? t : (t < HIT + STOP ? HIT : t - STOP);
            const on = t >= SLIDE && t < GONE;
            fade(dim, 0.45 * seg(t, DIM_IN, DIM_IN + 60) * (1 - seg(t, EXIT, EXIT + 140)));
            // 加速して滑り込み、叩きつけて止まる → 反動 → 見得 → 縦に潰れて消える
            const startX = hx + side * (W / 2 + PANEL.w * 0.2);
            const inK = tween(t, SLIDE, HIT, 'power2_in');
            const recoil = t >= HIT ? Math.sin(seg(u, HIT, HIT + 90) * Math.PI) * (1 - seg(u, HIT, HIT + 90)) : 0;
            const x = startX + (hx - startX) * inK + side * 14 * recoil;
            const out = tween(t, EXIT, GONE, 'power3_in');
            place(g, x, CY + (PANEL.nameH / 2) * out, { sx: 1 + 0.12 * out, sy: Math.max(0.001, 1 - out) });
            fade(g, on ? 1 : 0);
            fade(base.mesh, on ? 1 : 0);
            fade(small.mesh, on ? 1 : 0);
            fade(flash, t >= HIT && t < HIT + 34 ? 0.5 : 0);
            const len = t < HIT ? W * 0.45 * seg(t, SLIDE, SLIDE + 50) : W * 0.45 * (1 - seg(u, HIT, HIT + 80));
            trails.forEach(({ m, k }) => {
              const tail = x + side * PANEL.w / 2;
              place(m, tail + side * len / 2, frameCY + k * PANEL.frameH, { sx: Math.max(0.001, len), sy: k === 0.05 ? 6 : 3 });
              fade(m, on && len > 1 ? 0.9 : 0);
            });
          },
        };
      },
    },
  ],
  });
};

const layerOf = (spec, id) => spec.layers.filter((l) => l.id === id);

/** **motion**（`<situation>_motion.js`）。盤のカードが浮いて開く / くいっと回る */
export const posChangeMotionSpec = (variant) => {
  const s = specOf(variant);
  return {
    label: s.label,
    color: s.color,
    duration: s.motionDuration,
    sounds: s.motionSounds,
    tweaks: s.tweaks,
    layers: layerOf(s, 'card'),
  };
};

/** **effect**（`<situation>_effect.js`）。変わったあとのパネル（`panel` のときだけ帯を作る） */
export const posChangeEffectSpec = (variant) => {
  const s = specOf(variant);
  return {
    label: `${s.label}（パネル）`,
    color: s.color,
    opens: ({ payload }) => (payload?.panel ? payload : null),
    duration: () => PANEL_D,
    sounds: s.effectSounds,
    scene: s.scene,
    layers: layerOf(s, 'panel'),
  };
};
